import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, message, Row, Col, Avatar, Divider, Descriptions, Upload, Image, Space, Popconfirm } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, GlobalOutlined, LockOutlined, SaveOutlined, UploadOutlined, PictureOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const Profile = () => {
    const { user, setUser } = useAuth();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [uploadingQR, setUploadingQR] = useState(false);
    const [uploadingSignature, setUploadingSignature] = useState(false);
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    const handleImageUpload = async (file, imageType) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('imageType', imageType);

        try {
            if (imageType === 'qrCode') {
                setUploadingQR(true);
            } else {
                setUploadingSignature(true);
            }

            const response = await api.post('/upload/profile-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const imageUrl = response.data.data.fileUrl;

            // Update form field
            if (imageType === 'qrCode') {
                profileForm.setFieldsValue({ qrCodeImage: imageUrl });
            } else {
                profileForm.setFieldsValue({ doctorSignature: imageUrl });
            }

            message.success('Image uploaded successfully');
            return false; // Prevent default upload behavior
        } catch (error) {
            message.error(error.response?.data?.message || 'Upload failed');
            return false;
        } finally {
            if (imageType === 'qrCode') {
                setUploadingQR(false);
            } else {
                setUploadingSignature(false);
            }
        }
    };

    const handleImageDelete = async (imageType) => {
        const fieldName = imageType === 'qrCode' ? 'qrCodeImage' : 'doctorSignature';
        const fileUrl = profileForm.getFieldValue(fieldName);

        if (!fileUrl) {
            return;
        }

        try {
            if (imageType === 'qrCode') {
                setUploadingQR(true);
            } else {
                setUploadingSignature(true);
            }

            await api.delete('/upload/profile-image', {
                data: { fileUrl }
            });

            // Clear form field
            profileForm.setFieldsValue({ [fieldName]: null });
            message.success('Image deleted successfully');
        } catch (error) {
            message.error(error.response?.data?.message || 'Delete failed');
        } finally {
            if (imageType === 'qrCode') {
                setUploadingQR(false);
            } else {
                setUploadingSignature(false);
            }
        }
    };

    const handleUpdateProfile = async (values) => {
        setLoading(true);
        try {
            const response = await api.put('/auth/profile', values);
            // Merge, never replace: anything the endpoint leaves out (branchId,
            // permissions...) must survive, or branch features vanish until re-login.
            setUser({ ...user, ...response.data.data });
            message.success(t('success'));
            setEditMode(false);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (values) => {
        setLoading(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword
            });
            message.success('Password changed successfully');
            passwordForm.resetFields(['currentPassword', 'newPassword', 'confirmPassword']);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Row gutter={24}>
                <Col xs={24} lg={8}>
                    <Card
                        style={{
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #05668D 0%, #028090 100%)',
                            border: 'none',
                            color: 'white'
                        }}
                    >
                        <Avatar
                            size={120}
                            style={{
                                background: 'white',
                                color: '#028090',
                                fontSize: '48px',
                                fontWeight: 'bold',
                                border: '4px solid #F0F3BD',
                                marginBottom: 16
                            }}
                        >
                            {user?.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <h2 style={{ color: 'white', marginBottom: 8 }}>{user?.name}</h2>
                        <p style={{ color: '#F0F3BD', fontSize: '16px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {user?.role}
                        </p>
                        <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                        <Descriptions column={1} labelStyle={{ color: '#F0F3BD' }} contentStyle={{ color: 'white' }}>
                            <Descriptions.Item label={<PhoneOutlined />}>{user?.phoneNumber}</Descriptions.Item>
                            <Descriptions.Item label={<MailOutlined />}>{user?.email || 'Not set'}</Descriptions.Item>
                            <Descriptions.Item label={<GlobalOutlined />}>{user?.language?.toUpperCase()}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>

                <Col xs={24} lg={16}>
                    <Card
                        title={t('profileSettings')}
                        extra={
                            !editMode && (
                                <Button type="primary" onClick={() => setEditMode(true)}>
                                    Edit Profile
                                </Button>
                            )
                        }
                        style={{ marginBottom: 24 }}
                    >
                        <Form
                            form={profileForm}
                            layout="vertical"
                            initialValues={{
                                name: user?.name,
                                phoneNumber: user?.phoneNumber,
                                email: user?.email,
                                language: user?.language,
                                specialization: user?.specialization,
                                specialization_ku: user?.specialization_ku,
                                specialization_ar: user?.specialization_ar,
                                address: user?.address,
                                clinicName: user?.clinicName,
                                clinicAddress: user?.clinicAddress,
                                clinicPhone: user?.clinicPhone,
                                clinicDetail: user?.clinicDetail,
                                nameKurdish: user?.nameKurdish,
                                qrCodeImage: user?.qrCodeImage,
                                certificates: user?.certificates,
                                doctorSignature: user?.doctorSignature
                            }}
                            onFinish={handleUpdateProfile}
                        >
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="name"
                                        label={t('name')}
                                        rules={[{ required: true, message: 'Please input your name!' }]}
                                    >
                                        <Input prefix={<UserOutlined />} disabled={!editMode} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="phoneNumber"
                                        label={t('phoneNumber')}
                                        rules={[{ required: true, message: 'Please input your phone number!' }]}
                                    >
                                        <Input prefix={<PhoneOutlined />} disabled={!editMode} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="email" label={t('email')}>
                                        <Input prefix={<MailOutlined />} type="email" disabled={!editMode} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="language" label={t('language')}>
                                        <Select disabled={!editMode}>
                                            <Select.Option value="en">{t('english')}</Select.Option>
                                            <Select.Option value="ar">{t('arabic')}</Select.Option>
                                            <Select.Option value="ku">{t('kurdish')}</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                {user?.role === 'doctor' && (
                                    <>
                                        <Col xs={24}>
                                            <Divider orientation="left">Doctor Information</Divider>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="nameKurdish" label="Name (Kurdish)">
                                                <Input placeholder="ناوی دکتۆر" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="specialization" label={t('specialization')}>
                                                <Input placeholder="Specialization (English)" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="specialization_ku" label="Specialization (Kurdish)">
                                                <Input placeholder="پسپۆڕی" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="specialization_ar" label="Specialization (Arabic)">
                                                <Input placeholder="التخصص" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Divider orientation="left">Clinic Information</Divider>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="clinicName" label="Clinic Name">
                                                <Input disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="clinicPhone" label="Clinic Phone">
                                                <Input disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item name="clinicAddress" label="Clinic Address">
                                                <Input.TextArea rows={2} disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item name="clinicDetail" label="Clinic Details">
                                                <Input.TextArea rows={3} placeholder="Additional clinic information" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Divider orientation="left">Professional Details</Divider>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="certificates" label="Certificates">
                                                <Input.TextArea rows={2} placeholder="List your certificates" disabled={!editMode} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="qrCodeImage" label="QR Code Image">
                                                <div style={{ textAlign: 'center' }}>
                                                    {profileForm.getFieldValue('qrCodeImage') ? (
                                                        <div>
                                                            <Image
                                                                src={profileForm.getFieldValue('qrCodeImage')}
                                                                alt="QR Code"
                                                                width={150}
                                                                height={150}
                                                                style={{ objectFit: 'contain', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                                                            />
                                                            {editMode && (
                                                                <div style={{ marginTop: '8px' }}>
                                                                    <Space>
                                                                        <Upload
                                                                            accept="image/*"
                                                                            showUploadList={false}
                                                                            beforeUpload={(file) => handleImageUpload(file, 'qrCode')}
                                                                        >
                                                                            <Button
                                                                                icon={<UploadOutlined />}
                                                                                loading={uploadingQR}
                                                                            >
                                                                                Replace
                                                                            </Button>
                                                                        </Upload>
                                                                        <Popconfirm
                                                                            title="Delete QR Code"
                                                                            description="Are you sure you want to delete this QR code?"
                                                                            onConfirm={() => handleImageDelete('qrCode')}
                                                                            okText="Yes"
                                                                            cancelText="No"
                                                                        >
                                                                            <Button
                                                                                danger
                                                                                icon={<DeleteOutlined />}
                                                                                loading={uploadingQR}
                                                                            >
                                                                                Delete
                                                                            </Button>
                                                                        </Popconfirm>
                                                                    </Space>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        editMode && (
                                                            <Upload
                                                                accept="image/*"
                                                                showUploadList={false}
                                                                beforeUpload={(file) => handleImageUpload(file, 'qrCode')}
                                                            >
                                                                <Button
                                                                    icon={<UploadOutlined />}
                                                                    loading={uploadingQR}
                                                                    size="large"
                                                                    style={{ height: '150px', width: '150px' }}
                                                                >
                                                                    <div>Upload<br />QR Code</div>
                                                                </Button>
                                                            </Upload>
                                                        )
                                                    )}
                                                </div>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="doctorSignature" label="Signature Image">
                                                <div style={{ textAlign: 'center' }}>
                                                    {profileForm.getFieldValue('doctorSignature') ? (
                                                        <div>
                                                            <Image
                                                                src={profileForm.getFieldValue('doctorSignature')}
                                                                alt="Signature"
                                                                width={150}
                                                                height={150}
                                                                style={{ objectFit: 'contain', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                                                            />
                                                            {editMode && (
                                                                <div style={{ marginTop: '8px' }}>
                                                                    <Space>
                                                                        <Upload
                                                                            accept="image/*"
                                                                            showUploadList={false}
                                                                            beforeUpload={(file) => handleImageUpload(file, 'signature')}
                                                                        >
                                                                            <Button
                                                                                icon={<UploadOutlined />}
                                                                                loading={uploadingSignature}
                                                                            >
                                                                                Replace
                                                                            </Button>
                                                                        </Upload>
                                                                        <Popconfirm
                                                                            title="Delete Signature"
                                                                            description="Are you sure you want to delete this signature?"
                                                                            onConfirm={() => handleImageDelete('signature')}
                                                                            okText="Yes"
                                                                            cancelText="No"
                                                                        >
                                                                            <Button
                                                                                danger
                                                                                icon={<DeleteOutlined />}
                                                                                loading={uploadingSignature}
                                                                            >
                                                                                Delete
                                                                            </Button>
                                                                        </Popconfirm>
                                                                    </Space>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        editMode && (
                                                            <Upload
                                                                accept="image/*"
                                                                showUploadList={false}
                                                                beforeUpload={(file) => handleImageUpload(file, 'signature')}
                                                            >
                                                                <Button
                                                                    icon={<UploadOutlined />}
                                                                    loading={uploadingSignature}
                                                                    size="large"
                                                                    style={{ height: '150px', width: '150px' }}
                                                                >
                                                                    <div>Upload<br />Signature</div>
                                                                </Button>
                                                            </Upload>
                                                        )
                                                    )}
                                                </div>
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                            </Row>
                            {editMode && (
                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Save Changes
                                    </Button>
                                    <Button style={{ marginLeft: 8 }} onClick={() => setEditMode(false)}>
                                        Cancel
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </Card>

                    <Card title="Change Password">
                        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
                            <Form.Item
                                name="currentPassword"
                                label="Current Password"
                                rules={[{ required: true, message: 'Please input your current password!' }]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>
                            <Form.Item
                                name="newPassword"
                                label="New Password"
                                rules={[
                                    { required: true, message: 'Please input your new password!' },
                                    { min: 6, message: 'Password must be at least 6 characters!' }
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>
                            <Form.Item
                                name="confirmPassword"
                                label="Confirm New Password"
                                dependencies={['newPassword']}
                                rules={[
                                    { required: true, message: 'Please confirm your password!' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Passwords do not match!'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Change Password
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Profile;
